import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';

const LABEL_MAP = {
  'Age_Group': 'Age Group',
  'age_group': 'Age Group',
  'gender': 'Gender',
  'Party': 'Party',
  'Homeowner': 'Homeowner',
  'vote_propensity_score': 'Vote Propensity',
  'ethnicity': 'Ethnicity',
  'Race': 'Race',
  'Education': 'Education',
  'Income': 'Income',
  'MaritalStatus': 'Marital Status',
  'Congressional_District': 'Congressional District',
  'State_Senate': 'State Senate',
  'State_House': 'State House',
};

function cleanLabel(raw) {
  if (!raw) return raw;
  // Question dimension — show the label part
  if (raw.startsWith('q:')) {
    const parts = raw.split(':', 3);
    return parts[2] || raw;
  }
  if (LABEL_MAP[raw]) return LABEL_MAP[raw];
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * CrosstabDisplay - Renders crosstab results with statistics
 */
function CrosstabDisplay({ crosstab, index }) {
  if (crosstab.error) {
    return (
      <Box
        p={5}
        bg="red.50"
        borderRadius="md"
        borderWidth="1px"
        borderColor="red.200"
      >
        <Text color="red.700" fontWeight="medium">
          Error generating crosstab: {crosstab.error}
        </Text>
        <Text fontSize="sm" color="red.600" mt={1}>
          Question {crosstab.question_id}, Dimension: {cleanLabel(crosstab.dimension)}
        </Text>
      </Box>
    );
  }

  const {
    question_text,
    dimensions,
    data,
    statistics,
    weighted,
    sample_sizes,
  } = crosstab;

  const dimension = dimensions[0]; // For 1-way crosstabs
  const dimensionLabel = cleanLabel(dimension);

  // Extract unique choices from data
  const choices = data && Object.keys(data).length > 0
    ? Object.keys(Object.values(data)[0])
    : [];

  const demographicGroups = data ? Object.keys(data) : [];

  return (
    <Box
      p={6}
      bg="white"
      borderRadius="lg"
      borderWidth="1px"
      borderColor="gray.200"
      shadow="sm"
    >
      {/* Header */}
      <VStack align="start" spacing={2} mb={4}>
        <HStack>
          <Badge colorScheme="blue">Crosstab #{index + 1}</Badge>
          {weighted && <Badge colorScheme="purple">Weighted</Badge>}
          {dimension && dimension.startsWith('q:') && (
            <Badge colorScheme="green">Question Break</Badge>
          )}
        </HStack>
        <Heading size="sm">{question_text}</Heading>
        <Text fontSize="sm" color="gray.600">
          Dimension: <strong>{dimensionLabel}</strong>
        </Text>
      </VStack>

      <Divider mb={4} />

      {/* Crosstab Table */}
      {demographicGroups.length > 0 ? (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr bg="gray.100">
                <Th>{dimensionLabel}</Th>
                {choices.map(choice => (
                  <Th key={choice} textAlign="center">
                    {choice}
                  </Th>
                ))}
                <Th textAlign="center">Sample Size</Th>
              </Tr>
            </Thead>
            <Tbody>
              {demographicGroups.map(group => {
                const rowData = data[group];
                const sampleSize = sample_sizes?.[dimension]?.[group] || 0;
                const isTotal = group === 'Total';

                return (
                  <Tr
                    key={group}
                    bg={isTotal ? 'gray.50' : undefined}
                    borderBottomWidth={isTotal ? '2px' : undefined}
                    borderBottomColor={isTotal ? 'gray.300' : undefined}
                  >
                    <Td fontWeight={isTotal ? 'bold' : 'medium'}>
                      {group}
                    </Td>
                    {choices.map(choice => {
                      const cellData = rowData[choice];
                      const count = cellData?.count || 0;
                      const percentage = cellData?.percentage || 0;
                      const ciLower = cellData?.ci_lower;
                      const ciUpper = cellData?.ci_upper;

                      return (
                        <Td key={choice} textAlign="center">
                          <VStack spacing={0}>
                            <Text fontWeight={isTotal ? 'bold' : 'bold'}>
                              {percentage.toFixed(1)}%
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              (n={Math.round(count)})
                            </Text>
                            {!isTotal && ciLower !== null && ciUpper !== null && (
                              <Text fontSize="xs" color="gray.500">
                                [{ciLower.toFixed(1)}, {ciUpper.toFixed(1)}]
                              </Text>
                            )}
                          </VStack>
                        </Td>
                      );
                    })}
                    <Td textAlign="center" fontWeight={isTotal ? 'bold' : 'medium'}>
                      {isTotal
                        ? Object.values(rowData).reduce((sum, cell) => sum + (cell?.count || 0), 0)
                        : sampleSize}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <Text color="gray.500">No data available for this crosstab</Text>
      )}

      {/* Statistical Tests */}
      {statistics && Object.keys(statistics).length > 0 && !statistics.error && (
        <Accordion allowToggle mt={4}>
          <AccordionItem>
            <AccordionButton bg="background.shade2" _hover={{ bg: 'background.shade3' }}>
              <Box flex="1" textAlign="left" fontWeight="medium">
                Statistical Tests
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                {statistics.chi_square !== undefined && statistics.chi_square !== null && (
                  <Stat>
                    <StatLabel fontSize="xs">Chi-Square</StatLabel>
                    <StatNumber fontSize="md">{statistics.chi_square.toFixed(3)}</StatNumber>
                    <StatHelpText fontSize="xs">
                      df = {statistics.degrees_of_freedom || 'N/A'}
                    </StatHelpText>
                  </Stat>
                )}

                {statistics.p_value !== undefined && statistics.p_value !== null && (
                  <Stat>
                    <StatLabel fontSize="xs">P-Value</StatLabel>
                    <StatNumber fontSize="md">
                      {statistics.p_value < 0.001 ? '< 0.001' : statistics.p_value.toFixed(4)}
                    </StatNumber>
                    <StatHelpText fontSize="xs">
                      <Badge
                        colorScheme={statistics.significant ? 'green' : 'gray'}
                        fontSize="xs"
                      >
                        {statistics.significant ? 'Significant' : 'Not Significant'}
                      </Badge>
                    </StatHelpText>
                  </Stat>
                )}

                {statistics.cramers_v !== undefined && statistics.cramers_v !== null && (
                  <Stat>
                    <StatLabel fontSize="xs">Cramér's V</StatLabel>
                    <StatNumber fontSize="md">{statistics.cramers_v.toFixed(3)}</StatNumber>
                    <StatHelpText fontSize="xs">
                      Effect size
                    </StatHelpText>
                  </Stat>
                )}

                {statistics.significant !== undefined && (
                  <Stat>
                    <StatLabel fontSize="xs">Interpretation</StatLabel>
                    <StatHelpText fontSize="xs">
                      {statistics.significant ? (
                        <Text color="green.700">
                          Responses vary significantly across {dimensionLabel} groups
                          {statistics.p_value !== null && (
                            <> (p {statistics.p_value < 0.05 ? '< 0.05' : `= ${statistics.p_value.toFixed(3)}`})</>
                          )}
                        </Text>
                      ) : (
                        <Text color="gray.600">
                          No significant difference across {dimensionLabel} groups
                        </Text>
                      )}
                    </StatHelpText>
                  </Stat>
                )}
              </SimpleGrid>

              <Box mt={4} p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="xs" color="gray.700">
                  <strong>Reading the table:</strong> Percentages show the proportion of each
                  group that selected each answer. Confidence intervals (in brackets)
                  show the range where the true population percentage likely falls.
                  {statistics.significant && (
                    <> The chi-square test indicates that responses differ significantly
                    across {dimensionLabel} groups.</>
                  )}
                </Text>
              </Box>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </Box>
  );
}

export default CrosstabDisplay;
